import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Eye, Pencil, Plus, Search, Trash2, Truck } from 'lucide-react';
import { listShipmentPlans, deleteShipmentPlan, ShipmentPlan as ShipmentPlanRow } from '@/integrations/supabase/shipmentPlanService';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { CreateShipmentPlanDialog } from '@/components/CreateShipmentPlanDialog';
import { EditShipmentPlanDialog } from '@/components/EditShipmentPlanDialog';

const PAGE_SIZE = 50;

function formatVNDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari sets navigator.standalone; other engines expose display-mode.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayStandalone = window.matchMedia?.('(display-mode: standalone)').matches === true;
  return iosStandalone || displayStandalone;
}

export default function ShipmentPlan() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShipmentPlanRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShipmentPlanRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['shipment-plans', search],
    queryFn: () => listShipmentPlans({ search, limit: PAGE_SIZE }),
    staleTime: 60_000,
  });

  const plans = useMemo(() => data?.data ?? [], [data]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteShipmentPlan(id),
    onSuccess: async () => {
      toast({ title: 'Đã xóa báo cáo' });
      setDeleteTarget(null);
      await queryClient.refetchQueries({ queryKey: ['shipment-plans'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Xóa thất bại', description: err.message, variant: 'destructive' });
    },
  });

  const handleView = (plan: ShipmentPlanRow) => {
    const url = `/shipment-plan/view/${plan.id}`;
    // In an installed PWA (iOS Safari standalone, etc.) window.open often
    // hands the URL to an external browser, which loses the auth session.
    // Navigate within the same window instead.
    if (isStandalonePWA()) {
      navigate(url);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-full bg-background overflow-y-auto">
      <div className="max-w-7xl mx-auto w-full p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Truck className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold">Shipment Plan</h1>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Tạo báo cáo
          </Button>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo tên báo cáo..."
            className="pl-9"
          />
        </div>

        <div className="rounded-md border bg-card">
          <div className="grid grid-cols-12 gap-2 border-b px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
            <div className="col-span-2">Ngày</div>
            <div className="col-span-7">Tên báo cáo</div>
            <div className="col-span-3 text-right">Hành động</div>
          </div>

          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : plans.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              Chưa có báo cáo Shipment Plan nào.
            </div>
          ) : (
            <ul className="divide-y">
              {plans.map((plan) => {
                const isOwner = user?.id === plan.auth_user_id;
                return (
                  <li key={plan.id} className="grid grid-cols-12 items-center gap-2 px-4 py-3">
                    <div className="col-span-2 text-sm">{formatVNDate(plan.report_date)}</div>
                    <div className="col-span-7 text-sm font-medium truncate" title={plan.report_name}>
                      {plan.report_name}
                      {plan.summary && (
                        <span className="ml-2 text-xs text-muted-foreground line-clamp-1">{plan.summary}</span>
                      )}
                    </div>
                    <div className="col-span-3 flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => handleView(plan)} className="gap-1">
                        <Eye className="h-3.5 w-3.5" /> View
                      </Button>
                      {isOwner && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setEditTarget(plan)}
                            aria-label="Sửa"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteTarget(plan)}
                            aria-label="Xóa"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <CreateShipmentPlanDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditShipmentPlanDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        plan={editTarget}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xóa báo cáo Shipment Plan?</AlertDialogTitle>
            <AlertDialogDescription>
              Bản ghi sẽ bị xóa khỏi danh sách. File trên Google Drive vẫn được giữ.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
